import React, { useState, useEffect, useRef, useCallback } from 'react';
import { incluyeTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Car,
    Bus,
    MapPin,
    Clock,
    Calendar,
    Navigation,
    User,
    Phone,
    MessageSquare,
    Send,
    CheckCircle,
    Loader2,
    History,
    X,
    Building,
    Route,
    Crosshair,
    ClipboardList,
    Repeat,
    UsersRound,
    Search,
    ArrowUpDown,
    XCircle,
    Shield,
    Star,
    Layers,
    Minus,
    Plus
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import Modal from './common/Modal';
import toast from 'react-hot-toast';
import TaxiManagementView from './TaxiManagementView';

const EMERGENCY_CONTACTS = [
    { name: 'Juan Carlos Da Silva', phone: '0412-6203472' },
    { name: 'Juan Carlos Da Silva', phone: '0412-7234511' },
    { name: 'Oceánica', phone: '0800-OCEANICA' },
];

// Fix para iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Pins estilo Google Maps con letras A/B
const createGooglePin = (letter, color) => L.divIcon({
    html: `<div style="position:relative; width:28px; height:40px; filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
        <svg viewBox="0 0 28 40" width="28" height="40" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0z" fill="${color}"/>
            <circle cx="14" cy="14" r="10" fill="white"/>
            <text x="14" y="18.5" text-anchor="middle" font-size="14" font-weight="700" font-family="Arial,sans-serif" fill="${color}">${letter}</text>
        </svg>
    </div>`,
    className: '',
    iconSize: [28, 40],
    iconAnchor: [14, 40],
});

const originIcon = createGooglePin('A', '#34A853');
const destinationIcon = createGooglePin('B', '#EA4335');

// CSS para animaciones
const mapStyles = document.createElement('style');
mapStyles.textContent = `
@keyframes route-dash {
    to { stroke-dashoffset: -20; }
}
.leaflet-overlay-pane svg .route-animated {
    stroke-dasharray: 10 10;
    animation: route-dash 0.8s linear infinite;
}
`;
if (!document.getElementById('taxi-map-styles')) {
    mapStyles.id = 'taxi-map-styles';
    document.head.appendChild(mapStyles);
}

// Haversine distance (km)
const haversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Lo que recepcion carga del taxi asignado (proveedor, conductor, vehiculo) y con
// quien se comparte el viaje, para que el solicitante sepa que carro lo busca.
const DatosTaxiAsignado = ({ req }) => {
    const companeros = (req.companeros || []).filter(c => c.nombre);
    const hayConductor = req.driver || req.driverPhone || req.driverPlate;
    if (!hayConductor && !req.proveedor && companeros.length === 0) return null;
    return (
        <div className="mt-2.5 space-y-1.5">
            {(hayConductor || req.proveedor) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                    {req.proveedor && <span className="font-medium text-purple-600">{req.proveedor}</span>}
                    {req.driver && <span className="flex items-center gap-1"><User size={11} className="text-gray-400" />{req.driver}</span>}
                    {req.driverPhone && (
                        <a href={`tel:${req.driverPhone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                            <Phone size={11} />{req.driverPhone}
                        </a>
                    )}
                    {/* driverPlate guarda el modelo del vehiculo (la placa no se carga) */}
                    {req.driverPlate && <span className="flex items-center gap-1"><Car size={11} className="text-gray-400" />{req.driverPlate}</span>}
                </div>
            )}
            {companeros.length > 0 && (
                <div className="flex items-start gap-1.5 px-2.5 py-2 bg-indigo-50 rounded-lg text-xs text-indigo-700">
                    <UsersRound size={12} className="mt-0.5 shrink-0" />
                    <span>
                        <span className="font-semibold">Viaje compartido con </span>
                        {companeros.map(c => `${c.nombre}${c.destination ? ` (va a ${c.destination})` : ''}`).join(', ')}
                    </span>
                </div>
            )}
        </div>
    );
};

// Capacidad (pasajeros, contando a quien pide): carro hasta 4; van de 4 a 12.
const CAPACIDAD_CARRO = 4;
const CAPACIDAD_VAN = 12;

// "18:00" -> "6:00 PM"
const hora12 = (hhmm) => {
    if (!hhmm) return '-';
    const [h, m] = hhmm.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const LocationPicker = ({ mode, onLocationSelect }) => {
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        if (mode) {
            container.style.cursor = 'crosshair';
        } else {
            container.style.cursor = '';
        }
        return () => { container.style.cursor = ''; };
    }, [mode, map]);
    useMapEvents({
        click(e) {
            if (mode) onLocationSelect(e.latlng);
        },
    });
    return null;
};

const MapBoundsUpdater = ({ origin, destination, routePath }) => {
    const map = useMap();
    useEffect(() => {
        if (routePath && routePath.length > 2) {
            const bounds = L.latLngBounds(routePath);
            map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
        } else if (origin && destination) {
            const bounds = L.latLngBounds([origin, destination]);
            map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
        } else if (origin) {
            map.setView(origin, 15);
        } else if (destination) {
            map.setView(destination, 15);
        }
    }, [map, origin, destination, routePath]);
    return null;
};

// Re-mide el mapa cuando cambia la altura del contenedor (evita el área gris de
// Leaflet). Espera a que termine la transición de altura antes de invalidar.
const MapResizeInvalidator = ({ height }) => {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 320);
        return () => clearTimeout(t);
    }, [map, height]);
    return null;
};

const PREDEFINED_LOCATIONS = [
    { id: 'cdn', name: 'Sede CDN (Guacara)', address: 'Pruinca, Guacara 2015, Carabobo, Venezuela', lat: 10.237071802505122, lng: -67.86119517468343 },
    { id: 'ca1', name: 'Sede CA1 (Valencia)', address: 'Calle 149 Uslar, Valencia 2001, Carabobo, Venezuela', lat: 10.21865476305515, lng: -68.01304830351928 },
    { id: 'caracas', name: 'Sede Caracas', address: 'Torre Galipan, Caracas 1060, Miranda, Venezuela', lat: 10.490828231651808, lng: -66.86367816544895 },
];

// Time Picker compacto con dos columnas (hora | minutos)
const TimePicker = ({ value, onChange, label, accentColor = 'blue', dropUp = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const hourScrollRef = useRef(null);
    const selectedHour = value ? parseInt(value.split(':')[0]) : null;
    const selectedMin = value ? parseInt(value.split(':')[1]) : null;

    const hours = Array.from({ length: 18 }, (_, i) => i + 5); // 5-22
    const minutes = [0, 15, 30, 45];

    const formatDisplay = (val) => {
        if (!val) return null;
        const [h, m] = val.split(':').map(Number);
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const period = h >= 12 ? 'PM' : 'AM';
        return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
    };

    const handleSelect = (h, m) => {
        const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        onChange(val);
        setOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll a la hora seleccionada al abrir
    useEffect(() => {
        if (open && hourScrollRef.current && selectedHour !== null) {
            const idx = hours.indexOf(selectedHour);
            if (idx >= 0) hourScrollRef.current.scrollTop = Math.max(0, idx * 36 - 36);
        }
    }, [open, selectedHour]);

    const colors = {
        blue: { bg: 'bg-blue-600', hover: 'hover:bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-500' },
        amber: { bg: 'bg-amber-500', hover: 'hover:bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-500' },
    }[accentColor];

    return (
        <div ref={ref} className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm transition-all text-left ${open ? `ring-2 ${colors.ring} border-transparent bg-white` : 'hover:border-gray-300'}`}
            >
                <Clock size={15} className="text-gray-400" />
                {value ? (
                    <span className="font-medium text-gray-900">{formatDisplay(value)}</span>
                ) : (
                    <span className="text-gray-400">Seleccionar hora</span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -5, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -5, scale: 0.98 }}
                        className={`absolute z-50 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden w-full min-w-[200px] ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}
                    >
                        <div className="flex" style={{ height: '220px' }}>
                            {/* Columna de horas */}
                            <div ref={hourScrollRef} className="flex-1 overflow-y-auto border-r border-gray-100 py-1 scrollbar-thin">
                                {hours.map(h => {
                                    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                    const period = h >= 12 ? 'PM' : 'AM';
                                    const isSelected = h === selectedHour;
                                    return (
                                        <button
                                            key={h}
                                            type="button"
                                            onClick={() => handleSelect(h, selectedMin ?? 0)}
                                            className={`w-full px-3 py-2 text-sm text-center transition-colors ${isSelected ? `${colors.bg} text-white font-medium` : `text-gray-700 ${colors.hover}`}`}
                                        >
                                            {h12} {period}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Columna de minutos */}
                            <div className="flex-1 flex flex-col justify-center py-1">
                                {minutes.map(m => {
                                    const isSelected = m === selectedMin;
                                    return (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => handleSelect(selectedHour ?? 8, m)}
                                            className={`w-full px-3 py-3 text-sm text-center transition-colors ${isSelected ? `${colors.bg} text-white font-medium` : `text-gray-700 ${colors.hover}`}`}
                                        >
                                            :{m.toString().padStart(2, '0')}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Buscador de empleados para pasajeros
const PassengerSearch = ({ value, onChange, empleados, placeholder }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filtered, setFiltered] = useState([]);
    const [confirmed, setConfirmed] = useState(!!value);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (search.length >= 2) {
            const results = empleados.filter(emp =>
                incluyeTexto(emp.nombre, search)
            ).slice(0, 6);
            setFiltered(results);
            setIsOpen(results.length > 0);
        } else {
            setFiltered([]);
            setIsOpen(false);
        }
    }, [search, empleados]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setIsOpen(false);
                // Si dejó texto sin seleccionar, confirmar como texto libre (sin ficha)
                if (search.trim() && !confirmed) {
                    onChange(search.trim(), null);
                    setConfirmed(true);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [search, confirmed, onChange]);

    // Sincronizar si el padre resetea el valor
    useEffect(() => {
        if (!value) { setSearch(''); setConfirmed(false); }
    }, [value]);

    const handleSelect = (nombre, empleadoId = null) => {
        onChange(nombre, empleadoId);
        setSearch('');
        setConfirmed(true);
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange('');
        setSearch('');
        setConfirmed(false);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    if (confirmed && value) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                <User size={14} className="text-blue-600" />
                <span className="flex-1 text-sm font-medium text-gray-900 truncate">{value}</span>
                <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600">
                    <X size={14} />
                </button>
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && search.trim()) {
                            e.preventDefault();
                            handleSelect(search.trim(), null);
                        }
                    }}
                    onBlur={() => {
                        setTimeout(() => {
                            if (search.trim() && !confirmed) {
                                onChange(search.trim(), null);
                                setConfirmed(true);
                                setIsOpen(false);
                            }
                        }, 150);
                    }}
                    placeholder={placeholder}
                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                    onFocus={() => search.length >= 2 && setIsOpen(true)}
                />
            </div>
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-36 overflow-y-auto"
                >
                    {filtered.map(emp => (
                        <button
                            key={emp.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(emp.nombre, emp.id); }}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 transition text-sm"
                        >
                            <User size={12} className="text-gray-400" />
                            <span className="font-medium text-gray-900">{emp.nombre}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


const TaxiRequestView = () => {
    const { user } = useAuth();
    const isTaxiManager = user?.role === 'recepcion';

    // Leer tab de query params (para notificaciones inteligentes)
    const getInitialViewMode = () => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab === 'manage' && isTaxiManager) return 'manage';
        if (tab === 'aprobar') return 'aprobar';
        if (tab === 'history') return 'history';
        // Recepción/Admin/TI entran por defecto en gestionar
        if (isTaxiManager) return 'manage';
        return 'request';
    };

    const [viewMode, setViewMode] = useState(getInitialViewMode);

    const [formData, setFormData] = useState({
        origin: '',
        originCoords: null,
        destination: '',
        destinationCoords: null,
        date: new Date().toISOString().split('T')[0],
        time: '',
        vehicleType: 'carro',
        passengers: 1,
        passengerNames: [],
        notes: '',
        phone: '',
    });

    const [requests, setRequests] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [lastRequest, setLastRequest] = useState(null);
    const [pickingMode, setPickingMode] = useState(null);
    const [similares, setSimilares] = useState([]);
    const [focusedField, setFocusedField] = useState(null); // 'origin' | 'destination'
    const [routeInfo, setRouteInfo] = useState(null);
    const [needsReturn, setNeedsReturn] = useState(false);
    const [returnTime, setReturnTime] = useState('');
    const [mapType, setMapType] = useState('roadmap');

    // Direcciones favoritas
    const [favoritas, setFavoritas] = useState([]);
    const [savingFavorite, setSavingFavorite] = useState(null); // 'origin' | 'destination'
    const [favoriteName, setFavoriteName] = useState('');

    // Calificación post-viaje
    const [sinCalificar, setSinCalificar] = useState([]);
    const [ratingTrip, setRatingTrip] = useState(null);
    const [ratingStars, setRatingStars] = useState(0);
    const [ratingComment, setRatingComment] = useState('');
    const [submittingRating, setSubmittingRating] = useState(false);

    const originRef = useRef(null);
    const destRef = useRef(null);
    const formPanelRef = useRef(null);
    const mapContainerRef = useRef(null);
    const [mapHeight, setMapHeight] = useState(600);

    const mapCenter = [10.21865476305515, -68.01304830351928];
    const [routePath, setRoutePath] = useState(null);
    const [addressResults, setAddressResults] = useState([]);
    const [searchingAddress, setSearchingAddress] = useState(false);

    // Sincronizar altura del mapa con el panel del formulario, sin pasar del
    // alto del viewport (el mapa es sticky: se queda fijo en vez de estirarse).
    useEffect(() => {
        if (!formPanelRef.current) return;
        const cap = () => Math.max(420, window.innerHeight - 32);
        const apply = (h) => setMapHeight(Math.min(h, cap()));
        const observer = new ResizeObserver(([entry]) => apply(entry.contentRect.height));
        observer.observe(formPanelRef.current);
        const onResize = () => formPanelRef.current && apply(formPanelRef.current.getBoundingClientRect().height);
        window.addEventListener('resize', onResize);
        return () => { observer.disconnect(); window.removeEventListener('resize', onResize); };
    }, []);

    // Calcular ruta con Google Directions (via backend proxy)
    useEffect(() => {
        if (!formData.originCoords || !formData.destinationCoords) {
            setRouteInfo(null);
            setRoutePath(null);
            return;
        }
        const [oLat, oLng] = formData.originCoords;
        const [dLat, dLng] = formData.destinationCoords;

        axiosInstance.get('/api/taxi-requests/directions', {
            params: { olat: oLat, olng: oLng, dlat: dLat, dlng: dLng }
        })
            .then(res => {
                if (res.data.success && res.data.path) {
                    setRoutePath(res.data.path);
                    setRouteInfo({
                        distance: (res.data.distance / 1000).toFixed(1),
                        duration: Math.ceil(res.data.duration / 60),
                    });
                } else {
                    setRoutePath([[oLat, oLng], [dLat, dLng]]);
                    const distance = haversineDistance(oLat, oLng, dLat, dLng);
                    setRouteInfo({ distance: distance.toFixed(1), duration: Math.ceil(distance * 1.1) });
                }
            })
            .catch(() => {
                setRoutePath([[oLat, oLng], [dLat, dLng]]);
                const distance = haversineDistance(oLat, oLng, dLat, dLng);
                setRouteInfo({ distance: distance.toFixed(1), duration: Math.ceil(distance * 1.1) });
            });
    }, [formData.originCoords, formData.destinationCoords]);

    // Buscar direcciones con Google Places Autocomplete (via backend proxy)
    const searchAddress = useCallback(async (query) => {
        if (query.length < 3) { setAddressResults([]); return; }
        setSearchingAddress(true);
        try {
            const res = await axiosInstance.get('/api/taxi-requests/places/autocomplete', {
                params: { query }
            });
            if (res.data.success) {
                setAddressResults(res.data.results.map(r => ({
                    name: r.name,
                    description: r.description,
                    place_id: r.place_id,
                })));
            }
        } catch {
            setAddressResults([]);
        } finally {
            setSearchingAddress(false);
        }
    }, []);

    const fetchRequests = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/taxi-requests/historial');
            if (response.data.success) setRequests(response.data.solicitudes);
        } catch (error) {
            console.error('Error fetching requests:', error);
        }
    }, []);

    const fetchEmpleados = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/empleados');
            setEmpleados(response.data.map(emp => ({ id: emp.id, nombre: emp.nombre, ficha: emp.ficha })));
        } catch (error) {
            console.error('Error fetching empleados:', error);
        }
    }, []);

    const fetchFavoritas = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/taxi-requests/favoritas');
            if (res.data.success) setFavoritas(res.data.favoritas);
        } catch { }
    }, []);

    const handleSaveFavorite = async (field) => {
        const direccion = field === 'origin' ? formData.origin : formData.destination;
        const coords = field === 'origin' ? formData.originCoords : formData.destinationCoords;
        if (!direccion || !favoriteName.trim()) return;

        try {
            const res = await axiosInstance.post('/api/taxi-requests/favoritas', {
                nombre: favoriteName.trim(),
                direccion,
                lat: coords?.[0] || null,
                lng: coords?.[1] || null,
            });
            if (res.data.success) {
                setFavoritas(prev => [...prev, res.data.favorita]);
            }
        } catch (err) {
            console.error('Error guardando favorita:', err);
        } finally {
            setSavingFavorite(null);
            setFavoriteName('');
        }
    };

    const handleDeleteFavorite = async (id) => {
        try {
            await axiosInstance.delete(`/api/taxi-requests/favoritas/${id}`);
            setFavoritas(prev => prev.filter(f => f.id !== id));
        } catch (err) {
            console.error('Error eliminando favorita:', err);
        }
    };

    const fetchSinCalificar = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/taxi-requests/sin-calificar');
            if (res.data.success) setSinCalificar(res.data.solicitudes);
        } catch { }
    }, []);

    const handleSubmitRating = async () => {
        if (!ratingTrip || !ratingStars) return;
        try {
            setSubmittingRating(true);
            await axiosInstance.post(`/api/taxi-requests/${ratingTrip.id}/calificar`, {
                estrellas: ratingStars,
                comentario: ratingComment.trim() || null,
            });
            setSinCalificar(prev => prev.filter(s => s.id !== ratingTrip.id));
            setRatingTrip(null);
            setRatingStars(0);
            setRatingComment('');
            fetchRequests();
        } catch (err) {
            console.error('Error calificando:', err);
        } finally {
            setSubmittingRating(false);
        }
    };

    useEffect(() => { fetchRequests(); fetchEmpleados(); fetchFavoritas(); fetchSinCalificar(); }, [fetchRequests, fetchEmpleados, fetchFavoritas, fetchSinCalificar]);

    // Buscar similares
    useEffect(() => {
        const check = async () => {
            if (!formData.destination || !formData.date || !formData.time) { setSimilares([]); return; }
            try {
                const res = await axiosInstance.get('/api/taxi-requests/similares', {
                    params: { fecha: formData.date, hora: formData.time, destino: formData.destination }
                });
                if (res.data.success) setSimilares(res.data.similares);
            } catch { setSimilares([]); }
        };
        const timer = setTimeout(check, 500);
        return () => clearTimeout(timer);
    }, [formData.destination, formData.date, formData.time]);

    const reverseGeocode = async (lat, lng) => {
        try {
            const res = await axiosInstance.get('/api/taxi-requests/places/reverse-geocode', {
                params: { lat, lng }
            });
            if (res.data.success) return res.data.address;
            return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        } catch {
            return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        }
    };

    const handleSelectLocation = async (loc, field) => {
        if (loc.lat && loc.lng) {
            // Ubicación predefinida o favorita (ya tiene coordenadas)
            setFormData(prev => ({
                ...prev,
                [field]: loc.description || loc.name,
                [`${field}Coords`]: [loc.lat, loc.lng]
            }));
        } else if (loc.place_id) {
            // Resultado de Google Places — obtener coordenadas via place details
            setFormData(prev => ({ ...prev, [field]: loc.description || loc.name }));
            try {
                const res = await axiosInstance.get('/api/taxi-requests/places/details', {
                    params: { place_id: loc.place_id }
                });
                if (res.data.success && res.data.lat && res.data.lng) {
                    setFormData(prev => ({
                        ...prev,
                        [`${field}Coords`]: [res.data.lat, res.data.lng]
                    }));
                }
            } catch {
                // Coords no obtenidas, el usuario puede marcar en mapa
            }
        }
        setFocusedField(null);
    };

    const handleMapClick = async (latlng) => {
        const address = await reverseGeocode(latlng.lat, latlng.lng);
        if (pickingMode === 'origin') {
            setFormData(prev => ({
                ...prev,
                originCoords: [latlng.lat, latlng.lng],
                origin: prev.origin?.trim() ? prev.origin : address
            }));
        } else if (pickingMode === 'destination') {
            setFormData(prev => ({
                ...prev,
                destinationCoords: [latlng.lat, latlng.lng],
                destination: prev.destination?.trim() ? prev.destination : address
            }));
        }
        setPickingMode(null);
    };

    const handleMarkerDrag = async (field, e) => {
        const { lat, lng } = e.target.getLatLng();
        const address = await reverseGeocode(lat, lng);
        setFormData(prev => ({
            ...prev,
            [`${field}Coords`]: [lat, lng],
            [field]: address
        }));
    };

    const handleSwapLocations = () => {
        setFormData(prev => ({
            ...prev,
            origin: prev.destination,
            originCoords: prev.destinationCoords,
            destination: prev.origin,
            destinationCoords: prev.originCoords,
        }));
    };



    const isReturnTimeInvalid = needsReturn && formData.time && returnTime && returnTime <= formData.time;

    // Ajustar cantidad de pasajeros (1..12) preservando los nombres ya escritos.
    // Los nuevos pasajeros arrancan como empleado (toggle ON). El vehiculo se ajusta
    // solo: mas de 4 va en van y menos de 4 en carro (con 4 exactos se puede elegir).
    const setPassengerCount = (n) => {
        const count = Math.max(1, Math.min(n, CAPACIDAD_VAN));
        setFormData(prev => {
            const needed = count - 1;
            const names = prev.passengerNames.slice(0, needed);
            while (names.length < needed) {
                names.push({ nombre: '', esEmpleado: true, empleadoId: null });
            }
            const vehicleType = count > CAPACIDAD_CARRO ? 'van' : count < CAPACIDAD_CARRO ? 'carro' : prev.vehicleType;
            return { ...prev, passengers: count, passengerNames: names, vehicleType };
        });
    };

    const updatePassenger = (idx, patch) => {
        setFormData(prev => {
            const names = [...prev.passengerNames];
            names[idx] = { ...names[idx], ...patch };
            return { ...prev, passengerNames: names };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.origin || !formData.destination || !formData.date || !formData.time) return;
        if (needsReturn && !returnTime) return;
        if (isReturnTimeInvalid) {
            toast.error('La hora de retorno debe ser posterior a la hora de recogida.');
            return;
        }
        try {
            setSubmitting(true);
            const basePayload = {
                origin: formData.origin,
                originCoords: formData.originCoords,
                destination: formData.destination,
                destinationCoords: formData.destinationCoords,
                date: formData.date,
                vehicleType: formData.vehicleType,
                passengers: formData.passengers,
                passengerNames: formData.passengerNames.filter(p => p.nombre && p.nombre.trim()),
                phone: formData.phone,
            };

            // Viaje de ida
            const response = await axiosInstance.post('/api/taxi-requests', {
                ...basePayload,
                time: formData.time,
                notes: formData.notes || ''
            });

            // Viaje de retorno (vinculado a la ida)
            if (needsReturn && returnTime && response.data.solicitud) {
                await axiosInstance.post('/api/taxi-requests', {
                    ...basePayload,
                    origin: formData.destination,
                    originCoords: formData.destinationCoords,
                    destination: formData.origin,
                    destinationCoords: formData.originCoords,
                    time: returnTime,
                    notes: formData.notes || '',
                    retorno_de_id: response.data.solicitud.id
                });
            }

            if (response.data.success) {
                setLastRequest(response.data.solicitud);
                setShowSuccessModal(true);
                fetchRequests();
                setFormData({
                    origin: '', originCoords: null, destination: '', destinationCoords: null,
                    date: new Date().toISOString().split('T')[0], time: '',
                    vehicleType: 'carro', passengers: 1, passengerNames: [], notes: '', phone: '',
                });
                setNeedsReturn(false);
                setReturnTime('');
            }
        } catch (error) {
            console.error('Error submitting request:', error);
            toast.error(error.response?.data?.error || 'No se pudo enviar la solicitud de taxi');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending_approval: 'bg-orange-100 text-orange-700',
            pending: 'bg-amber-100 text-amber-700',
            confirmed: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700',
            cancelled: 'bg-red-100 text-red-700',
            rejected: 'bg-red-100 text-red-700',
        };
        const labels = {
            pending_approval: 'Esperando aprobación', pending: 'Pendiente', confirmed: 'Confirmado',
            completed: 'Completado', cancelled: 'Cancelado', rejected: 'Rechazada',
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    // Estado para aprobaciones de supervisor
    const [porAprobar, setPorAprobar] = useState([]);
    const [historialAprobaciones, setHistorialAprobaciones] = useState([]);
    const [loadingAprobar, setLoadingAprobar] = useState(false);
    const [updatingAprobar, setUpdatingAprobar] = useState(null);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [rechazandoId, setRechazandoId] = useState(null);

    const fetchPorAprobar = useCallback(async () => {
        try {
            setLoadingAprobar(true);
            const res = await axiosInstance.get('/api/taxi-requests/por-aprobar');
            if (res.data.success) {
                setPorAprobar(res.data.pendientes || res.data.solicitudes || []);
                setHistorialAprobaciones(res.data.historial || []);
            }
        } catch (err) {
            console.error('Error fetching por aprobar:', err);
        } finally {
            setLoadingAprobar(false);
        }
    }, []);

    useEffect(() => {
        if (viewMode === 'aprobar') fetchPorAprobar();
    }, [viewMode, fetchPorAprobar]);

    const handleAprobar = async (id) => {
        try {
            setUpdatingAprobar(id);
            await axiosInstance.post(`/api/taxi-requests/${id}/aprobar`);
            setPorAprobar(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            console.error('Error aprobando:', err);
        } finally {
            setUpdatingAprobar(null);
        }
    };

    const handleRechazar = async (id) => {
        try {
            setUpdatingAprobar(id);
            await axiosInstance.post(`/api/taxi-requests/${id}/rechazar`, { motivo: motivoRechazo });
            setPorAprobar(prev => prev.filter(s => s.id !== id));
            setRechazandoId(null);
            setMotivoRechazo('');
        } catch (err) {
            console.error('Error rechazando:', err);
        } finally {
            setUpdatingAprobar(null);
        }
    };

    // Tabs disponibles según rol
    const hasSupervisorItems = porAprobar.length > 0 || historialAprobaciones.length > 0;
    const getTabs = () => {
        const tabs = [];
        tabs.push({ key: 'request', label: 'Solicitar Taxi' });
        if (isTaxiManager) tabs.push({ key: 'manage', label: 'Gestionar Solicitudes' });
        if (!isTaxiManager && hasSupervisorItems) tabs.push({ key: 'aprobar', label: 'Mi equipo' });
        return tabs;
    };

    const ViewTabs = ({ current }) => {
        const tabs = getTabs();
        if (tabs.length <= 1) return null;
        return (
            <div className="flex bg-gray-100 rounded-lg p-1">
                {tabs.map(tab => (
                    <button key={tab.key} onClick={() => setViewMode(tab.key)}
                        className={`px-4 py-2 rounded-md font-medium transition-all ${
                            current === tab.key ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:text-gray-900'
                        }`}>
                        {tab.label}
                        {tab.key === 'aprobar' && porAprobar.length > 0 && current !== 'aprobar' && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                                {porAprobar.length}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        );
    };

    // Cargar conteo de aprobaciones al montar (para mostrar badge)
    useEffect(() => {
        if (user?.empleado_id && !isTaxiManager) {
            axiosInstance.get('/api/taxi-requests/por-aprobar')
                .then(res => {
                    if (res.data.success) {
                        setPorAprobar(res.data.pendientes || res.data.solicitudes || []);
                        setHistorialAprobaciones(res.data.historial || []);
                    }
                })
                .catch(() => {});
        }
    }, [user, isTaxiManager]);

    // Modo gestión (recepción)
    if (isTaxiManager && viewMode === 'manage') {
        return (
            <div className="max-w-[1400px] mx-auto animate-fadeIn pb-8">
                <header className="mb-6 pt-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h2 className="page-title">Solicitud de Taxi</h2>
                            <ViewTabs current="manage" />
                        </div>
                    </div>
                </header>
                <TaxiManagementView />
            </div>
        );
    }

    // Tarjeta de solicitud reutilizable para aprobaciones
    const ApprovalCard = ({ sol, showActions = false }) => (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <User size={16} className="text-gray-500" />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900 text-sm">{sol.empleado}</p>
                            {sol.departamento && <p className="text-xs text-gray-400">{sol.departamento}</p>}
                        </div>
                    </div>
                    {getStatusBadge(sol.status)}
                </div>

                <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{sol.origin}</span>
                    </div>
                    <div className="ml-1 border-l-2 border-dashed border-gray-200 h-2" />
                    <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{sol.destination}</span>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1 font-medium text-gray-700">
                        <Clock size={12} />
                        Ida {sol.time || '-'}
                    </span>
                    {sol.retorno && (
                        <span className="flex items-center gap-1 font-medium text-indigo-600">
                            <Repeat size={12} />
                            Vuelta {sol.retorno.time || '-'}
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {sol.date ? new Date(sol.date + 'T00:00:00').toLocaleDateString('es-VE') : '-'}
                    </span>
                    <span className="flex items-center gap-1">
                        <UsersRound size={12} />
                        {sol.passengers} {sol.passengers === 1 ? 'pasajero' : 'pasajeros'}
                    </span>
                </div>

                {sol.notes && (
                    <div className="mt-2 p-2 bg-amber-50 rounded-lg">
                        <p className="text-xs text-amber-700 flex items-start gap-1">
                            <MessageSquare size={12} className="mt-0.5 flex-shrink-0" />
                            {sol.notes}
                        </p>
                    </div>
                )}
            </div>

            {showActions && (
                <>
                    {rechazandoId === sol.id ? (
                        <div className="border-t border-gray-100 p-3 space-y-2">
                            <input
                                type="text"
                                value={motivoRechazo}
                                onChange={e => setMotivoRechazo(e.target.value)}
                                placeholder="Motivo del rechazo (opcional)"
                                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-red-400"
                                autoFocus
                            />
                            <div className="flex gap-2">
                                <button onClick={() => { setRechazandoId(null); setMotivoRechazo(''); }}
                                    className="flex-1 py-2 text-xs font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                                    Cancelar
                                </button>
                                <button onClick={() => handleRechazar(sol.id)}
                                    disabled={updatingAprobar === sol.id}
                                    className="flex-1 py-2 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-1">
                                    {updatingAprobar === sol.id ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                                    Confirmar rechazo
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="border-t border-gray-100 p-3 flex items-center gap-2">
                            <div className="flex-1" />
                            <button onClick={() => setRechazandoId(sol.id)}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                                <XCircle size={14} />
                                Rechazar
                            </button>
                            <button onClick={() => handleAprobar(sol.id)}
                                disabled={updatingAprobar === sol.id}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                                {updatingAprobar === sol.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                                Aprobar
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // Modo aprobaciones (supervisor)
    if (viewMode === 'aprobar') {
        return (
            <div>
                <ViewTabs current="aprobar" />
                <div className="max-w-[1400px] mx-auto animate-fadeIn pb-8">
                    <h2 className="page-title mb-4">Solicitudes de mi equipo</h2>

                    {/* Contactos de emergencia - Oceánica de Seguros */}
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield size={16} className="text-blue-600" />
                            <p className="text-sm font-semibold text-blue-900">Emergencias - Oceánica de Seguros, C.A.</p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            {EMERGENCY_CONTACTS.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-blue-800">
                                    <Phone size={10} />
                                    <span className="font-medium">{c.name}:</span>
                                    <a href={`tel:${c.phone}`} className="underline">{c.phone}</a>
                                </div>
                            ))}
                        </div>
                    </div>

                    {loadingAprobar ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 size={32} className="animate-spin text-gray-400" />
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Pendientes */}
                            {porAprobar.length > 0 ? (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                        Pendientes ({porAprobar.length})
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {porAprobar.map(sol => (
                                            <ApprovalCard key={sol.id} sol={sol} showActions />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Shield size={24} className="text-gray-300" />
                                    </div>
                                    <p className="text-gray-500">Tu equipo no tiene solicitudes pendientes</p>
                                </div>
                            )}

                            {/* Historial de aprobaciones */}
                            {historialAprobaciones.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                        Historial
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {historialAprobaciones.map(sol => (
                                            <ApprovalCard key={sol.id} sol={sol} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Modo historial (desde notificación)
    if (viewMode === 'history') {
        return (
            <div>
                <ViewTabs current="history" />
                <div className="max-w-[1400px] mx-auto animate-fadeIn pb-8">
                    <div className="flex justify-between items-center mb-6 pt-4">
                        <h2 className="page-title">Mis Solicitudes de Taxi</h2>
                        <button onClick={() => setViewMode('request')}
                            className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-blue-700 font-medium text-sm">
                            <Car size={18} />
                            Solicitar Taxi
                        </button>
                    </div>
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                        <div className="divide-y divide-gray-100">
                            {requests.length > 0 ? requests.map((req) => (
                                <div key={req.id} className="p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                                <span className="font-medium text-gray-900 text-sm">{req.origin}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mb-2.5">
                                                <div className="w-2 h-2 rounded-sm bg-blue-600" />
                                                <span className="font-medium text-gray-900 text-sm">{req.destination}</span>
                                            </div>
                                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                                <span className="flex items-center gap-1"><Calendar size={12} />{req.date ? new Date(req.date + 'T00:00:00').toLocaleDateString('es-VE') : '-'}</span>
                                                <span className="flex items-center gap-1"><Clock size={12} />Ida {hora12(req.time)}</span>
                                                {req.retorno && <span className="flex items-center gap-1 text-indigo-600"><Repeat size={12} />Vuelta {hora12(req.retorno.time)}</span>}
                                            </div>
                                            <DatosTaxiAsignado req={req} />
                                            {/* Calificación o botón calificar */}
                                            {req.calificacion ? (
                                                <div className="flex items-center gap-1.5 mt-2">
                                                    {[1,2,3,4,5].map(i => (
                                                        <Star key={i} size={12} className={i <= req.calificacion.estrellas ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
                                                    ))}
                                                    {req.calificacion.comentario && <span className="text-xs text-gray-400 ml-1 truncate">{req.calificacion.comentario}</span>}
                                                </div>
                                            ) : req.status === 'completed' && (
                                                <button
                                                    onClick={() => { setRatingTrip(req); setRatingStars(0); setRatingComment(''); }}
                                                    className="mt-2 flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium transition-colors"
                                                >
                                                    <Star size={12} /> Calificar viaje
                                                </button>
                                            )}
                                        </div>
                                        {getStatusBadge(req.status)}
                                    </div>
                                </div>
                            )) : (
                                <div className="p-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Car size={24} className="text-gray-400" />
                                    </div>
                                    <p className="text-gray-500">No tienes solicitudes previas</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto animate-fadeIn pb-8">
            {/* Header con tabs integrados */}
            <header className="mb-6 pt-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h2 className="page-title">Solicitud de Taxi</h2>
                        <ViewTabs current="request" />
                    </div>
                    <button onClick={() => setShowHistory(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors text-gray-700 font-medium text-sm">
                        <History size={18} />
                        Mis Solicitudes
                    </button>
                </div>
            </header>

            {/* Banner calificación pendiente */}
            {sinCalificar.length > 0 && !ratingTrip && (
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                                <Star size={20} className="text-amber-500" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-amber-900">
                                    {sinCalificar.length === 1 ? 'Tienes un viaje pendiente de calificar' : `Tienes ${sinCalificar.length} viajes pendientes de calificar`}
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    {sinCalificar[0].origin} → {sinCalificar[0].destination}
                                    {sinCalificar[0].proveedor && ` (${sinCalificar[0].proveedor})`}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => { setRatingTrip(sinCalificar[0]); setRatingStars(0); setRatingComment(''); }}
                            className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors"
                        >
                            Calificar
                        </button>
                    </div>
                </div>
            )}

            {/* Main Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

                {/* Panel izquierdo: Ruta + Detalles */}
                <div ref={formPanelRef} className="lg:col-span-5 xl:col-span-4 space-y-4">
                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Bloque de ruta estilo Uber */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                            <div className="flex items-center gap-4">
                                {/* Indicadores visuales */}
                                <div className="flex flex-col items-center py-1">
                                    <div className="w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20" />
                                    <div className="w-0.5 flex-1 min-h-[32px] bg-gradient-to-b from-emerald-400 to-blue-400 my-1" />
                                    <div className="w-3 h-3 rounded-sm bg-blue-600 ring-4 ring-blue-500/20" />
                                </div>

                                {/* Inputs */}
                                <div className="flex-1 space-y-2">
                                    <div className="relative">
                                        <input
                                            ref={originRef}
                                            type="text"
                                            value={formData.origin}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData(prev => ({ ...prev, origin: val, ...(val === '' ? { originCoords: null } : {}) }));
                                                setFocusedField('origin');
                                                clearTimeout(window._addrTimer);
                                                window._addrTimer = setTimeout(() => searchAddress(val), 400);
                                            }}
                                            onFocus={() => setFocusedField('origin')}
                                            onBlur={() => setTimeout(() => setFocusedField(null), 200)}
                                            placeholder="¿Dónde te recogemos?"
                                            className="w-full px-3 py-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-emerald-500/30 focus:outline-none transition-all"
                                        />
                                        {formData.origin && (
                                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, origin: '', originCoords: null }))}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                                                <X size={14} />
                                            </button>
                                        )}

                                        {/* Dropdown origen */}
                                        <AnimatePresence>
                                            {focusedField === 'origin' && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -5 }}
                                                    className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                                                >
                                                    {/* Favoritas */}
                                                    {favoritas.filter(f => !formData.origin || f.nombre.toLowerCase().includes(formData.origin.toLowerCase()) || f.direccion.toLowerCase().includes(formData.origin.toLowerCase())).length > 0 && (
                                                        <>
                                                            <div className="px-3 py-1.5 bg-amber-50/50">
                                                                <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider">Mis direcciones</p>
                                                            </div>
                                                            {favoritas.filter(f => !formData.origin || f.nombre.toLowerCase().includes(formData.origin.toLowerCase()) || f.direccion.toLowerCase().includes(formData.origin.toLowerCase())).map(fav => (
                                                                <button key={`fav-o-${fav.id}`} type="button"
                                                                    onClick={() => handleSelectLocation({ name: fav.direccion, lat: fav.lat, lng: fav.lng }, 'origin')}
                                                                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left">
                                                                    <Star size={16} className="text-amber-500 flex-shrink-0 fill-amber-500" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900">{fav.nombre}</p>
                                                                        <p className="text-xs text-gray-500 truncate">{fav.direccion}</p>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                    {PREDEFINED_LOCATIONS.filter(l => !formData.origin || l.name.toLowerCase().includes(formData.origin.toLowerCase())).map(loc => (
                                                        <button key={loc.id} type="button"
                                                            onClick={() => handleSelectLocation(loc, 'origin')}
                                                            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                                                            <Building size={16} className="text-emerald-500 flex-shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{loc.name}</p>
                                                                <p className="text-xs text-gray-500">{loc.address}</p>
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {addressResults.length > 0 && formData.origin?.length >= 3 && (
                                                        <>
                                                            <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
                                                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Resultados</p>
                                                            </div>
                                                            {addressResults.map((r, i) => (
                                                                <button key={`addr-${i}`} type="button"
                                                                    onClick={() => { handleSelectLocation(r, 'origin'); setAddressResults([]); }}
                                                                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                                                                    <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900">{r.name}</p>
                                                                        {r.description !== r.name && <p className="text-xs text-gray-500 truncate">{r.description}</p>}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                    {searchingAddress && (
                                                        <div className="px-3 py-2 flex items-center gap-2 text-gray-400 border-t border-gray-100">
                                                            <Loader2 size={14} className="animate-spin" />
                                                            <span className="text-xs">Buscando...</span>
                                                        </div>
                                                    )}
                                                    <button type="button"
                                                        onClick={() => { setPickingMode('origin'); setFocusedField(null); }}
                                                        className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left border-t border-gray-100">
                                                        <Crosshair size={16} className="text-amber-500 flex-shrink-0" />
                                                        <p className="text-sm font-medium text-amber-700">Seleccionar en el mapa</p>
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    <div className="relative">
                                        <input
                                            ref={destRef}
                                            type="text"
                                            value={formData.destination}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData(prev => ({ ...prev, destination: val, ...(val === '' ? { destinationCoords: null } : {}) }));
                                                setFocusedField('destination');
                                                clearTimeout(window._addrTimer);
                                                window._addrTimer = setTimeout(() => searchAddress(val), 400);
                                            }}
                                            onFocus={() => setFocusedField('destination')}
                                            onBlur={() => setTimeout(() => setFocusedField(null), 200)}
                                            placeholder="¿A dónde vas?"
                                            className="w-full px-3 py-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500/30 focus:outline-none transition-all"
                                        />
                                        {formData.destination && (
                                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, destination: '', destinationCoords: null }))}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                                                <X size={14} />
                                            </button>
                                        )}

                                        {/* Dropdown destino */}
                                        <AnimatePresence>
                                            {focusedField === 'destination' && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -5 }}
                                                    className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                                                >
                                                    {/* Favoritas */}
                                                    {favoritas.filter(f => !formData.destination || f.nombre.toLowerCase().includes(formData.destination.toLowerCase()) || f.direccion.toLowerCase().includes(formData.destination.toLowerCase())).length > 0 && (
                                                        <>
                                                            <div className="px-3 py-1.5 bg-amber-50/50">
                                                                <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider">Mis direcciones</p>
                                                            </div>
                                                            {favoritas.filter(f => !formData.destination || f.nombre.toLowerCase().includes(formData.destination.toLowerCase()) || f.direccion.toLowerCase().includes(formData.destination.toLowerCase())).map(fav => (
                                                                <button key={`fav-d-${fav.id}`} type="button"
                                                                    onClick={() => handleSelectLocation({ name: fav.direccion, lat: fav.lat, lng: fav.lng }, 'destination')}
                                                                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left">
                                                                    <Star size={16} className="text-amber-500 flex-shrink-0 fill-amber-500" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900">{fav.nombre}</p>
                                                                        <p className="text-xs text-gray-500 truncate">{fav.direccion}</p>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                    {PREDEFINED_LOCATIONS.filter(l => !formData.destination || l.name.toLowerCase().includes(formData.destination.toLowerCase())).map(loc => (
                                                        <button key={loc.id} type="button"
                                                            onClick={() => handleSelectLocation(loc, 'destination')}
                                                            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                                                            <Building size={16} className="text-blue-500 flex-shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{loc.name}</p>
                                                                <p className="text-xs text-gray-500">{loc.address}</p>
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {addressResults.length > 0 && formData.destination?.length >= 3 && (
                                                        <>
                                                            <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
                                                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Resultados</p>
                                                            </div>
                                                            {addressResults.map((r, i) => (
                                                                <button key={`addr-d-${i}`} type="button"
                                                                    onClick={() => { handleSelectLocation(r, 'destination'); setAddressResults([]); }}
                                                                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                                                                    <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900">{r.name}</p>
                                                                        {r.description !== r.name && <p className="text-xs text-gray-500 truncate">{r.description}</p>}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </>
                                                    )}
                                                    {searchingAddress && (
                                                        <div className="px-3 py-2 flex items-center gap-2 text-gray-400 border-t border-gray-100">
                                                            <Loader2 size={14} className="animate-spin" />
                                                            <span className="text-xs">Buscando...</span>
                                                        </div>
                                                    )}
                                                    <button type="button"
                                                        onClick={() => { setPickingMode('destination'); setFocusedField(null); }}
                                                        className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left border-t border-gray-100">
                                                        <Crosshair size={16} className="text-amber-500 flex-shrink-0" />
                                                        <p className="text-sm font-medium text-amber-700">Seleccionar en el mapa</p>
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Botón intercambiar */}
                                {formData.origin && formData.destination && (
                                    <button type="button" onClick={handleSwapLocations}
                                        className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
                                        title="Intercambiar">
                                        <ArrowUpDown size={16} />
                                    </button>
                                )}
                            </div>

                            {/* Hint: marcar en mapa si falta coordenadas */}
                            <AnimatePresence>
                                {formData.origin && formData.destination && (!formData.originCoords || !formData.destinationCoords) && !routeInfo && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <button
                                                type="button"
                                                onClick={() => { setPickingMode(!formData.originCoords ? 'origin' : 'destination'); }}
                                                className="w-full bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3 hover:bg-amber-100 transition-colors text-left"
                                            >
                                                <Crosshair size={18} className="text-amber-500 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-medium text-amber-800">Marca la ubicación en el mapa</p>
                                                    <p className="text-xs text-amber-600">
                                                        Haz click en el mapa para ubicar {!formData.originCoords ? 'el origen' : 'el destino'} y calcular la ruta
                                                    </p>
                                                </div>
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Route info inline */}
                            <AnimatePresence>
                                {routeInfo && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-3 flex items-center justify-around text-white">
                                                <div className="text-center">
                                                    <p className="text-blue-200 text-[10px] uppercase tracking-wider">Distancia</p>
                                                    <p className="text-lg font-bold">{routeInfo.distance} km</p>
                                                </div>
                                                <div className="w-px h-8 bg-white/20" />
                                                <div className="text-center">
                                                    <p className="text-blue-200 text-[10px] uppercase tracking-wider">Tiempo est.</p>
                                                    <p className="text-lg font-bold">~{routeInfo.duration} min</p>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Guardar favorita — inline */}
                        <AnimatePresence>
                            {savingFavorite && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Star size={16} className="text-amber-500 fill-amber-500" />
                                            <p className="text-sm font-medium text-gray-900">Guardar como favorita</p>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2 truncate">
                                            {savingFavorite === 'origin' ? formData.origin : formData.destination}
                                        </p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={favoriteName}
                                                onChange={(e) => setFavoriteName(e.target.value)}
                                                placeholder='Ej: "Mi casa"'
                                                maxLength={50}
                                                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:bg-white focus:outline-none transition-all"
                                                autoFocus
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveFavorite(savingFavorite); } }}
                                            />
                                            <button type="button" onClick={() => handleSaveFavorite(savingFavorite)}
                                                disabled={!favoriteName.trim()}
                                                className="px-3 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-40">
                                                Guardar
                                            </button>
                                            <button type="button" onClick={() => { setSavingFavorite(null); setFavoriteName(''); }}
                                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Acciones favoritas: guardar origen/destino actual + gestionar */}
                        {(formData.origin || formData.destination || favoritas.length > 0) && !savingFavorite && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {formData.origin && favoritas.length < 2 && !favoritas.some(f => f.direccion === formData.origin) && !PREDEFINED_LOCATIONS.some(l => l.name === formData.origin || l.address === formData.origin) && (
                                    <button type="button" onClick={() => { setSavingFavorite('origin'); setFavoriteName(''); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors">
                                        <Star size={12} /> Guardar origen
                                    </button>
                                )}
                                {formData.destination && favoritas.length < 2 && !favoritas.some(f => f.direccion === formData.destination) && !PREDEFINED_LOCATIONS.some(l => l.name === formData.destination || l.address === formData.destination) && (
                                    <button type="button" onClick={() => { setSavingFavorite('destination'); setFavoriteName(''); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors">
                                        <Star size={12} /> Guardar destino
                                    </button>
                                )}
                                {favoritas.map(fav => (
                                    <span key={fav.id} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-gray-600">
                                        <Star size={10} className="text-amber-500 fill-amber-500" />
                                        {fav.nombre}
                                        <button type="button" onClick={() => handleDeleteFavorite(fav.id)}
                                            className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors">
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Fecha y hora */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Fecha</label>
                                    <div className="relative">
                                        <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="date"
                                            value={formData.date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/30 focus:bg-white focus:outline-none transition-all"
                                        />
                                    </div>
                                </div>
                                <TimePicker
                                    label="Hora de recogida"
                                    value={formData.time}
                                    onChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
                                    accentColor="blue"
                                />
                            </div>
                        </div>

                        {/* Retorno */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Repeat size={15} className="text-amber-500" />
                                    <span className="text-sm font-medium text-gray-700">Necesito retorno</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setNeedsReturn(!needsReturn); if (needsReturn) setReturnTime(''); }}
                                    className={`relative w-11 h-6 rounded-full transition-colors ${needsReturn ? 'bg-amber-500' : 'bg-gray-200'}`}
                                >
                                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${needsReturn ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                            {needsReturn && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <TimePicker
                                        label="Hora de retorno"
                                        value={returnTime}
                                        onChange={setReturnTime}
                                        accentColor="amber"
                                        dropUp
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1.5">Se creará una segunda solicitud con la ruta invertida</p>
                                    {isReturnTimeInvalid && (
                                        <p className="text-[11px] text-red-500 mt-1.5">
                                            La hora de retorno debe ser posterior a la hora de recogida.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Pasajeros + Detalles */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
                            {/* Vehículo */}
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-2">Vehículo</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[{ key: 'carro', label: 'Carro', Icon: Car }, { key: 'van', label: 'Van', Icon: Bus }].map(({ key, label, Icon }) => {
                                        // Carro hasta 4 pasajeros; van desde 4.
                                        const noPermitido = key === 'carro'
                                            ? formData.passengers > CAPACIDAD_CARRO
                                            : formData.passengers < CAPACIDAD_CARRO;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                disabled={noPermitido}
                                                onClick={() => setFormData(prev => ({ ...prev, vehicleType: key }))}
                                                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${formData.vehicleType === key
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/30'
                                                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <Icon size={16} /> {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    Carro: hasta {CAPACIDAD_CARRO} pasajeros · Van: de {CAPACIDAD_CARRO} a {CAPACIDAD_VAN}. Se ajusta según los pasajeros.
                                </p>
                            </div>

                            {/* Pasajeros */}
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-2">Pasajeros</label>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setPassengerCount(formData.passengers - 1)}
                                        disabled={formData.passengers <= 1}
                                        aria-label="Quitar pasajero"
                                        className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                                    >
                                        <Minus size={16} />
                                    </button>
                                    <span className="w-8 text-center text-lg font-semibold text-gray-900 tabular-nums">{formData.passengers}</span>
                                    <button
                                        type="button"
                                        onClick={() => setPassengerCount(formData.passengers + 1)}
                                        disabled={formData.passengers >= CAPACIDAD_VAN}
                                        aria-label="Agregar pasajero"
                                        className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                                    >
                                        <Plus size={16} />
                                    </button>
                                    <span className="text-xs text-gray-400">máx. {CAPACIDAD_VAN}</span>
                                </div>
                            </div>

                            {/* Pasajeros adicionales: empleado (buscador) o externo (texto libre) */}
                            {formData.passengers > 1 && (
                                <div className="space-y-2.5">
                                    <p className="text-xs text-gray-500">Pasajeros adicionales:</p>
                                    {formData.passengerNames.map((p, idx) => (
                                        <div key={idx} className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] font-medium text-gray-400">Pasajero {idx + 2}</span>
                                                <div className="flex rounded-lg bg-gray-100 p-0.5">
                                                    {[{ k: true, l: 'Empleado' }, { k: false, l: 'Externo' }].map(({ k, l }) => (
                                                        <button
                                                            key={l}
                                                            type="button"
                                                            onClick={() => updatePassenger(idx, { esEmpleado: k, nombre: '', empleadoId: null })}
                                                            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${p.esEmpleado === k
                                                                ? 'bg-white text-blue-600 shadow-sm'
                                                                : 'text-gray-500 hover:text-gray-700'
                                                                }`}
                                                        >
                                                            {l}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {p.esEmpleado ? (
                                                <PassengerSearch
                                                    value={p.nombre}
                                                    empleados={empleados}
                                                    placeholder="Buscar empleado"
                                                    onChange={(nombre, empleadoId) => updatePassenger(idx, { nombre, empleadoId })}
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={p.nombre}
                                                    onChange={(e) => updatePassenger(idx, { nombre: e.target.value })}
                                                    placeholder="Nombre del pasajero externo"
                                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:outline-none transition-all"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Teléfono */}
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1.5">Teléfono de contacto</label>
                                <div className="relative">
                                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder="04241234567"
                                        className="w-full pl-9 pr-3 py-2.5 bg-gray-50 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/30 focus:bg-white focus:outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Notas */}
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1.5">Instrucciones adicionales</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Parada adicional, equipaje..."
                                    rows={2}
                                    className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/30 focus:bg-white focus:outline-none transition-all resize-none"
                                />
                            </div>
                        </div>

                        {/* Compartir taxi */}
                        {similares.length > 0 && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-2xl">
                                <div className="flex items-start gap-2.5">
                                    <UsersRound size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-blue-900">¿Compartir taxi?</p>
                                        <p className="text-xs text-blue-700 mt-0.5">
                                            {similares.length === 1
                                                ? `${similares[0].empleado} va al mismo destino a las ${hora12(similares[0].time)}.`
                                                : `${similares.length} personas van al mismo destino.`}
                                            {' '}Coordina con recepción.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Submit */}
                        <motion.button
                            type="submit"
                            disabled={submitting || !formData.origin || !formData.destination || !formData.date || !formData.time || (needsReturn && (!returnTime || isReturnTimeInvalid))}
                            className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-semibold shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                            whileHover={{ scale: submitting ? 1 : 1.01 }}
                            whileTap={{ scale: submitting ? 1 : 0.99 }}
                        >
                            {submitting ? (
                                <><Loader2 size={18} className="animate-spin" /> Procesando...</>
                            ) : (
                                <><Send size={18} /> Solicitar Taxi</>
                            )}
                        </motion.button>
                    </form>
                </div>

                {/* Panel derecho: Mapa */}
                <div className="lg:col-span-7 xl:col-span-8 relative">
                    {/* Indicador modo selección — pill flotante */}
                    <AnimatePresence>
                        {pickingMode && (
                            <motion.div
                                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-3"
                            >
                                <div className={`flex items-center gap-2.5 px-5 py-3 rounded-full shadow-xl backdrop-blur-sm text-white font-medium text-sm ${
                                    pickingMode === 'origin' ? 'bg-emerald-500/90' : 'bg-blue-500/90'
                                }`}>
                                    <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
                                    Toca el mapa para {pickingMode === 'origin' ? 'el punto de recogida' : 'el destino'}
                                </div>
                                <button onClick={() => setPickingMode(null)}
                                    className="p-2.5 bg-black/60 backdrop-blur-sm rounded-full hover:bg-black/80 text-white transition-colors shadow-xl">
                                    <X size={16} />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className={`relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 sticky top-4 ${pickingMode ? 'ring-4 ring-amber-400/30' : ''}`}
                        style={{ height: `${mapHeight}px` }}>
                        <MapContainer
                            center={mapCenter}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                            className="rounded-2xl"
                        >
                            <ReactLeafletGoogleLayer key={mapType} apiKey="AIzaSyCcb31TvDfo2X1wMaymRFG7sSLHXEmhIgc" type={mapType} />
                            <MapBoundsUpdater origin={formData.originCoords} destination={formData.destinationCoords} routePath={routePath} />
                            <MapResizeInvalidator height={mapHeight} />
                            <LocationPicker mode={pickingMode} onLocationSelect={handleMapClick} />

                            {formData.originCoords && (
                                <Marker
                                    position={formData.originCoords}
                                    icon={originIcon}
                                    draggable={true}
                                    eventHandlers={{ dragend: (e) => handleMarkerDrag('origin', e) }}
                                >
                                    <Popup><strong className="text-green-600">A</strong> — {formData.origin}</Popup>
                                </Marker>
                            )}
                            {formData.destinationCoords && (
                                <Marker
                                    position={formData.destinationCoords}
                                    icon={destinationIcon}
                                    draggable={true}
                                    eventHandlers={{ dragend: (e) => handleMarkerDrag('destination', e) }}
                                >
                                    <Popup><strong className="text-red-600">B</strong> — {formData.destination}</Popup>
                                </Marker>
                            )}
                            {routePath && (
                                <>
                                    <Polyline
                                        positions={routePath}
                                        pathOptions={{ color: '#4285F4', weight: 6, opacity: 0.4, lineCap: 'round' }}
                                    />
                                    <Polyline
                                        positions={routePath}
                                        pathOptions={{ color: '#4285F4', weight: 4, opacity: 1, lineCap: 'round', dashArray: '10 10', className: 'route-animated' }}
                                    />
                                </>
                            )}
                        </MapContainer>
                        {/* Toggle satélite/mapa */}
                        <button
                            type="button"
                            onClick={() => setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap')}
                            className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors border border-gray-200"
                        >
                            <Layers size={16} className="text-gray-600" />
                            <span className="text-xs font-medium text-gray-700">{mapType === 'roadmap' ? 'Satélite' : 'Mapa'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal Historial */}
            <Modal isOpen={showHistory} onClose={() => setShowHistory(false)} size="2xl">
                <Modal.Header onClose={() => setShowHistory(false)}>
                    <div className="flex items-center gap-3">
                        <History size={20} className="text-gray-500" />
                        <span>Historial de Solicitudes</span>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="max-h-[70vh] overflow-y-auto">
                        {requests.length > 0 ? (() => {
                            // Agrupar por mes/año
                            const grouped = {};
                            requests.forEach(req => {
                                if (!req.date) return;
                                const [y, m, d] = req.date.split('-').map(Number);
                                const key = `${y}-${String(m).padStart(2, '0')}`;
                                const monthLabel = new Date(y, m - 1).toLocaleDateString('es-VE', { month: 'long', year: 'numeric' });
                                if (!grouped[key]) grouped[key] = { label: monthLabel, items: [] };
                                grouped[key].items.push({ ...req, _day: d, _month: m, _year: y });
                            });
                            const to12h = (t) => {
                                if (!t) return '';
                                const [h, m] = t.split(':').map(Number);
                                const suffix = h >= 12 ? 'PM' : 'AM';
                                const h12 = h % 12 || 12;
                                return { hour: h12, min: String(m).padStart(2, '0'), suffix };
                            };
                            const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

                            return sortedKeys.map(key => (
                                <div key={key}>
                                    <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm px-5 py-2 border-b border-gray-200">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{grouped[key].label}</span>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {grouped[key].items.map((request) => {
                                            const hasRetorno = request.retorno;
                                            const statusColors = {
                                                pending_approval: { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400' },
                                                pending: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-400' },
                                                confirmed: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-400' },
                                                completed: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-400' },
                                                cancelled: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-400' },
                                                rejected: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-400' },
                                            };
                                            const sc = statusColors[request.status] || statusColors.pending;

                                            return (
                                                <div key={request.id} className="px-5 py-4 hover:bg-gray-50/50 transition-colors">
                                                    <div className="flex items-start gap-4">
                                                        {/* Fecha + Hora */}
                                                        <div className="flex-shrink-0 w-16 text-center">
                                                            <div className="text-2xl font-bold text-gray-800 leading-none">{request._day}</div>
                                                            <div className="text-[10px] text-gray-400 font-medium uppercase mt-0.5">
                                                                {new Date(request._year, request._month - 1, request._day).toLocaleDateString('es-VE', { weekday: 'short' })}
                                                            </div>
                                                            {(() => {
                                                                const t = to12h(request.time);
                                                                return t ? (
                                                                    <div className="mt-1.5 bg-gray-100 rounded-md px-1.5 py-0.5">
                                                                        <span className="text-sm font-semibold text-gray-700">{t.hour}:{t.min}</span>
                                                                        <span className="text-[9px] font-medium text-gray-400 ml-0.5">{t.suffix}</span>
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                        </div>

                                                        {/* Ruta */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start gap-2">
                                                                <div className="flex flex-col items-center mt-1.5 flex-shrink-0">
                                                                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                                                    <div className="w-px h-4 bg-gray-300" />
                                                                    <div className="w-2 h-2 rounded-sm bg-blue-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{request.origin}</p>
                                                                    <p className="text-sm text-gray-500 truncate mt-1">{request.destination}</p>
                                                                </div>
                                                            </div>

                                                            {/* Meta info */}
                                                            <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                                                                {request.passengers > 1 && (
                                                                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                                                        <UsersRound size={11} className="text-gray-400" />
                                                                        {request.passengers}
                                                                    </span>
                                                                )}
                                                                {hasRetorno && (
                                                                    <span className="inline-flex items-center gap-1 text-xs text-indigo-500 font-medium">
                                                                        <Repeat size={11} />
                                                                        Ida y vuelta
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Retorno info */}
                                                            {hasRetorno && (
                                                                <div className="mt-2 ml-4 pl-3 border-l-2 border-indigo-200">
                                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                        <span className="font-medium text-indigo-600">Vuelta:</span>
                                                                        <Clock size={10} />
                                                                        {(() => { const t = to12h(request.retorno.time); return t ? `${t.hour}:${t.min} ${t.suffix}` : ''; })()}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <DatosTaxiAsignado req={request} />
                                                        </div>

                                                        {/* Status */}
                                                        <div className="flex-shrink-0">
                                                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.bg} ${sc.border}`}>
                                                                <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                                                {{ pending_approval: 'Esperando aprobación', pending: 'Pendiente', confirmed: 'Confirmado', completed: 'Completado', cancelled: 'Cancelado', rejected: 'Rechazada' }[request.status] || request.status}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ));
                        })() : (
                            <div className="p-12 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Car size={24} className="text-gray-400" />
                                </div>
                                <p className="text-gray-500">No tienes solicitudes previas</p>
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal Calificación */}
            <Modal isOpen={!!ratingTrip} onClose={() => { setRatingTrip(null); setRatingStars(0); setRatingComment(''); }} size="sm">
                <Modal.Header onClose={() => { setRatingTrip(null); setRatingStars(0); setRatingComment(''); }}>
                    <div className="flex items-center gap-2">
                        <Star size={20} className="text-amber-500" />
                        <span>Calificar viaje</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {ratingTrip && (
                        <div className="space-y-4">
                            <div className="p-3 bg-gray-50 rounded-xl text-sm">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    <span className="text-gray-700">{ratingTrip.origin}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-sm bg-blue-600" />
                                    <span className="text-gray-700">{ratingTrip.destination}</span>
                                </div>
                                {ratingTrip.proveedor && (
                                    <p className="text-xs text-purple-600 mt-2 font-medium">{ratingTrip.proveedor}</p>
                                )}
                            </div>

                            <div className="text-center">
                                <p className="text-sm text-gray-500 mb-3">¿Cómo fue el servicio?</p>
                                <div className="flex items-center justify-center gap-2">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setRatingStars(i)}
                                            className="transition-transform hover:scale-110"
                                        >
                                            <Star
                                                size={32}
                                                className={`transition-colors ${i <= ratingStars ? 'text-amber-400 fill-amber-400' : 'text-gray-200 hover:text-amber-200'}`}
                                            />
                                        </button>
                                    ))}
                                </div>
                                {ratingStars > 0 && (
                                    <p className="text-xs text-gray-400 mt-2">
                                        {['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente'][ratingStars]}
                                    </p>
                                )}
                            </div>

                            <div>
                                <textarea
                                    value={ratingComment}
                                    onChange={(e) => setRatingComment(e.target.value)}
                                    placeholder="Comentario (opcional)"
                                    rows={2}
                                    maxLength={500}
                                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:bg-white focus:outline-none transition-all resize-none"
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3 w-full">
                        <button
                            onClick={() => { setRatingTrip(null); setRatingStars(0); setRatingComment(''); }}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Omitir
                        </button>
                        <button
                            onClick={handleSubmitRating}
                            disabled={!ratingStars || submittingRating}
                            className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {submittingRating ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
                            Enviar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal Éxito */}
            <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} size="md">
                <Modal.Body className="p-8 text-center">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30"
                    >
                        <CheckCircle size={40} className="text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Solicitud Enviada!</h3>
                    <p className="text-gray-500 mb-6">
                        {lastRequest?.status === 'pending_approval'
                            ? 'Tu supervisor recibirá la solicitud para aprobarla.'
                            : 'Tu solicitud fue enviada a recepción para confirmación.'}
                    </p>
                    {lastRequest && (
                        <div className="bg-gray-50 rounded-2xl p-4 text-left mb-6">
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Origen:</span>
                                    <span className="font-medium text-gray-900">{lastRequest.origin}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Destino:</span>
                                    <span className="font-medium text-gray-900">{lastRequest.destination}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Fecha/Hora:</span>
                                    <span className="font-medium text-gray-900">{lastRequest.date} a las {hora12(lastRequest.time)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => setShowSuccessModal(false)}
                        className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-orange-500/30 transition-all"
                    >
                        Entendido
                    </button>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TaxiRequestView;
